import React, {useMemo} from 'react';
import styles from './BenefactorsTable.module.scss';
import {Avatar, TableColumnConfig, Text, Spin, Button} from '@gravity-ui/uikit';
import {formatDateToLocalDate, DataTable, formatPhoneNumber, useAvatarCache, getUncachedImageUrl} from '../../shared';
import {useNavigate} from 'react-router';
import {BenefactorModel} from '../../entities';
import {useInfiniteScroll} from '../../shared/hooks/useInfiniteScroll';

interface BenefactorsTableProps {
  benefactors: BenefactorModel[];
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
}

export const BenefactorsTable = ({
  benefactors,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}: BenefactorsTableProps) => {
  const navigate = useNavigate();
  const SKELETON_ROWS = 5;
  const {avatarCacheVersion} = useAvatarCache();

  const handleRowClick = (benefactor: BenefactorModel) => {
    navigate(`/accounts/benefactors/profile/${benefactor.id}`);
  };

  const columns = useMemo<TableColumnConfig<BenefactorModel>[]>(
    () => [
      {
        id: 'displayName',
        name: 'Display name',
        template: (item: BenefactorModel) => (
          <div className={styles.userCellContainer}>
            <Avatar
              imgUrl={getUncachedImageUrl(item.avatarUrl || '', avatarCacheVersion)}
              text={item.displayName ? item.displayName[0] : '?'}
              size="s"
            />
            <Text variant="body-1">{item.displayName}</Text>
          </div>
        ),
      },
      {
        id: 'realName',
        name: 'Real name',
        template: (item: BenefactorModel) => (
          <div className={styles.userCellContainer}>
            <Text variant="body-1">{item.realName}</Text>
          </div>
        ),
      },
      {
        id: 'phoneNumber',
        name: 'Телефон',
        template: (item: BenefactorModel) => formatPhoneNumber(item.phoneNumber),
      },
      {
        id: 'email',
        name: 'E-mail',
        template: (item: BenefactorModel) => item.email,
      },
      {
        id: 'createdAt',
        name: 'Дата регистрации',
        template: (item: BenefactorModel) => formatDateToLocalDate(item.createdAt),
      },
    ],
    [avatarCacheVersion],
  );

  const {loadMoreRef} = useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <div className={styles.tableContainer}>
      <DataTable<BenefactorModel>
        isLoading={isLoading && !isFetchingNextPage}
        data={benefactors || []}
        columns={columns}
        edgePadding={false}
        skeletonRowCount={SKELETON_ROWS}
        emptyStateTitle="Пользователь не найден"
        emptyStateDescription="Пользователь с таким именем или адресом почты не найден. Попробуйте изменить запрос"
        onRowClick={handleRowClick}
      />

      {isFetchingNextPage ? (
        <div className={styles.loadingSpinnerContainer}>
          <Spin size="m" />
        </div>
      ) : (
        hasNextPage && (
          <Button
            component={'div'}
            size="xl"
            width="max"
            view="normal"
            ref={loadMoreRef}
            className={styles.loadMoreTrigger}>
            <Text variant="body-2">Загрузить ещё</Text>
          </Button>
        )
      )}
    </div>
  );
};
